import homeScreen from '../images/home_screen.jpg';
import shopBg from '../images/shop_screen.jpg';
import dynamicDataImg from '../images/dynamic_data.jpg';
import workflowImg from '../images/workflow_automation.jpg';
import securityImg from '../images/security_lock.jpg';

import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SecurityIcon from '@mui/icons-material/Security';

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroBg}></div>
        <div style={styles.heroOverlay}>
          <h1 style={styles.heroTitle}>Asset Management. Reinvented.</h1>
          <p style={styles.heroSub}>
            Complete visibility into equipment, repairs, inventory, and shop
            performance.
          </p>
          <button style={styles.primaryButton}>Request Demo</button>
        </div>
      </section>

      {/* FEATURES */}
      <section style={styles.section}>
        <div style={styles.sectionBg}></div>

        <div style={styles.content}>
          <h2 style={styles.title}>Fully Customizable Database Platform</h2>

          <p style={styles.sub}>
            A flexible data engine designed to mold around your business; not
            the other way around. Define your own objects, workflows,
            permissions, and reporting logic with complete control.
          </p>

          <div style={styles.highlight}>
            No rigid templates. No forced workflows. Build exactly what your
            operation needs; from repair tracking to enterprise asset
            management.
          </div>

          <div style={styles.grid}>
            <FeatureCard
              img={dynamicDataImg}
              icon={<StorageIcon style={{ fontSize: 22 }} />}
              title="Dynamic Data Models"
              text="Create unlimited custom objects, fields, and relationships without rewriting code."
            />

            <FeatureCard
              img={workflowImg}
              icon={<AccountTreeIcon style={{ fontSize: 22 }} />}
              title="Workflow Automation"
              text="Configure status flows, approval chains, and calculation logic tailored to your exact process."
            />

            <FeatureCard
              img={securityImg}
              icon={<SecurityIcon style={{ fontSize: 22 }} />}
              title="Granular Security"
              text="Control visibility, editing rights, and access at the object, field, or user-role level."
            />
          </div>

          <button style={styles.primaryButton}>Request Demo</button>
        </div>
      </section>
    </>
  );
}

/* ---------- CARD COMPONENT ---------- */

function FeatureCard({ img, icon, title, text }) {
  return (
    <div style={styles.card}>
      <div style={{ ...styles.cardImageWrap, backgroundImage: `url(${img})` }}>
        <div style={styles.imageOverlay}></div>
        <div style={styles.iconCircle}>{icon}</div>{' '}
      </div>
      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{title}</h3>
        <p style={styles.cardText}>{text}</p>
      </div>
    </div>
  );
}

/* ---------- STYLES ---------- */

const styles = {
  /* HERO */
  hero: {
    position: 'relative',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  heroBg: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${homeScreen})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'blur(8px) brightness(0.6)',
    transform: 'scale(1.1)',
  },

  heroOverlay: {
    position: 'relative',
    zIndex: 2,
    background: 'rgba(0,0,0,0.65)',
    padding: '70px',
    borderRadius: '20px',
    textAlign: 'center',
    color: '#fff',
    maxWidth: '800px',
  },

  heroTitle: {
    fontSize: '48px',
    fontWeight: '700',
    marginBottom: '20px',
  },

  heroSub: {
    fontSize: '18px',
    marginBottom: '30px',
    opacity: 0.9,
  },

  /* FEATURE SECTION */
  section: {
    position: 'relative',
    padding: '160px 40px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
  },

  sectionBg: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${shopBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'blur(8px) brightness(0.6)',
    transform: 'scale(1.08)',
    zIndex: 0,
  },

  content: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '1200px',
    background: 'rgba(255,255,255,0.95)',
    padding: '80px',
    borderRadius: '24px',
    textAlign: 'center',
    boxShadow: '0 50px 100px rgba(0,0,0,0.35)',
  },

  title: {
    fontSize: '40px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#1e3a8a',
  },

  sub: {
    fontSize: '18px',
    color: '#334155',
    marginBottom: '50px',
    maxWidth: '850px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  highlight: {
    marginBottom: '70px',
    padding: '24px 40px',
    background: 'linear-gradient(90deg,#2563eb,#3b82f6)',
    borderRadius: '16px',
    fontWeight: '600',
    fontSize: '16px',
    color: '#fff',
    boxShadow: '0 20px 40px rgba(37,99,235,0.35)',
  },

  /* CARDS */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
    gap: '40px',
    marginBottom: '70px',
  },

  card: {
    borderRadius: '20px',
    overflow: 'hidden',
    background: '#ffffff',
    boxShadow: '0 25px 50px rgba(0,0,0,0.12)',
    transition: '0.3s ease',
    textAlign: 'left',
  },

  cardImageWrap: {
    position: 'relative',
    height: '200px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },

  imageOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.7))',
  },

  iconCircle: {
    position: 'absolute',
    bottom: '-30px',
    left: '30px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    color: '#fff',
    boxShadow: '0 10px 25px rgba(37,99,235,0.5)',
  },

  cardBody: {
    padding: '70px 30px 35px 30px',
  },

  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#0f172a',
  },

  cardText: {
    fontSize: '15px',
    color: '#475569',
    lineHeight: '1.7',
  },

  /* BUTTON */
  primaryButton: {
    padding: '16px 45px',
    background: 'linear-gradient(90deg,#2563eb,#3b82f6)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 20px 40px rgba(37,99,235,0.35)',
  },
};

styles.card[':hover'] = {
  transform: 'translateY(-12px)',
  boxShadow: '0 40px 80px rgba(0,0,0,0.25)',
};

styles.primaryButton[':hover'] = {
  transform: 'scale(1.05)',
};
